import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
// Assuming these exist in your project structure for schema validation
import { BaseData, Any, List, ModernFloatRangeExampleClass } from './BaseData.js';
import { RenderControlData } from './RenderControlData.js';

/**
 * LLM EXAMPLE JSON (ImageHero)
 * This is intentionally a real object (not a comment) so it stays in sync with defaults,
 * but can be overridden per-prop when using <ImageHero ... />.
 */
const IMAGE_HERO_EXAMPLE = {
  render_control_type: "product_shot_section",
  title: "Unleash Your Creative Potential, Faster Than Ever.",
  subtitle: "fragmint, our e-learning and studio software, allows us to share ai lessons, images, videos, and even web apps apps all without the need for any signup. ",
  imagePath: "/productshot_01.png", // Placeholder path for the complex UI image
  imageAltText: "Fragment UI diagram showing workflow and data flow.",
  buttonLabel: "LEARN MORE →",
  buttonHref: "/learn-more",
  // Optional styling properties, allowing full control from JSON
  titleColor: "#343a40", // Dark grey
  buttonBackgroundColor: "#FFD700", // Gold
  buttonTextColor: "#000000",      // Black
  buttonBorderColor: "#FFD700",    // Gold
  buttonHoverBackgroundColor: "#e6c200", // Slightly darker gold on hover
  buttonHoverTextColor: "#000000",
  paddingTop: "4rem",
  paddingBottom: "4rem",
  imageMarginBottom: "3rem",
  buttonMarginTop: "3rem",

  renderMode: "large"
};

const ProductShotSection = ({
  section = IMAGE_HERO_EXAMPLE
}) => {
  var title = section.title;
  var imagePath = section.imagePath;
  var imageAltText = section.imageAltText;
  var buttonLabel = section.buttonLabel;
  var buttonHref = section.buttonHref;
  var titleColor = section.titleColor;
  var subtitle = section.subtitle;
  var buttonBackgroundColor = section.buttonBackgroundColor;
  var buttonTextColor = section.buttonTextColor;
  var buttonBorderColor = section.buttonBorderColor;
  var buttonHoverBackgroundColor = section.buttonHoverBackgroundColor;
  var buttonHoverTextColor = section.buttonHoverTextColor;
  var paddingTop = section.paddingTop;
  var paddingBottom = section.paddingBottom;
  var imageMarginBottom = section.imageMarginBottom;
  var buttonMarginTop = section.buttonMarginTop;
  // Inline style for the main section container
  const sectionStyle = {
    paddingTop: paddingTop,
    paddingBottom: paddingBottom,
    backgroundColor: '#ffffff', // Assuming a white background as per image
    textAlign: 'center',
  };

  // Inline style for the title
  const titleStyle = {
    color: titleColor,
    fontSize: 'clamp(2rem, 5vw, 4.5rem)', // Responsive font size
    fontWeight: 800,
    marginBottom: '2rem', // Space below the title
    lineHeight: 1.1,
  };

  // Inline style for the image
  const imageStyle = {
    maxWidth: '100%',
    height: 'auto',
    display: 'block', // Ensures image takes up full width available
    margin: '0 auto', // Center the image horizontally
    marginBottom: imageMarginBottom, // Space below the image
  };

  // Inline style for the button, dynamically set from props
  const buttonDefaultStyle = {
    backgroundColor: buttonBackgroundColor,
    color: buttonTextColor,
    borderColor: buttonBorderColor,
    fontWeight: 'bold',
    padding: '1rem 3rem',
    fontSize: '1.25rem',
    marginTop: buttonMarginTop, // Space above the button
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
  };

  // Using useState to handle button hover effect
  const [isHovered, setIsHovered] = React.useState(false);

  const buttonDynamicStyle = {
    ...buttonDefaultStyle,
    backgroundColor: isHovered ? buttonHoverBackgroundColor : buttonBackgroundColor,
    color: isHovered ? buttonHoverTextColor : buttonTextColor,
    borderColor: isHovered ? buttonHoverBackgroundColor : buttonBorderColor, // Border also changes on hover
  };


  return (
    <section style={sectionStyle}>
      <Container>
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {/* Title */}
                <h1 className="display-2  mt-4">{title || 'missing title'}</h1>
                <p className="lead mt-5">
                  {subtitle || 'Learn how to deepen and widen your creative process'}
                </p>
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col xs={12}>
            {/* Image */}
            <img
              src={imagePath}
              alt={imageAltText}
              style={imageStyle}
            />
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col>
            {/* Button */}
            <Button
              href={buttonHref}
              style={buttonDynamicStyle}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {buttonLabel}
            </Button>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

/**
 * Defines the schema for ImageHero component data using RenderControlData base class.
 * This helps in validating the structure of the JSON payload.
 */
class ProductShotSectionData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    title: String,
    imagePath: String,
    imageAltText: String,
    buttonLabel: String,
    buttonHref: String,
    titleColor: String,
    buttonBackgroundColor: String,
    buttonTextColor: String,
    buttonBorderColor: String,
    buttonHoverBackgroundColor: String,
    buttonHoverTextColor: String,
    paddingTop: String,
    paddingBottom: String,
    imageMarginBottom: String,
    buttonMarginTop: String,
  });
}

export { ProductShotSection, RenderControlData, ProductShotSectionData };
export default ProductShotSection;